'use client'
import type { UserAccountPortfolio, UserProject } from '@repo/services'
import { useUserAuthDetails, useUserPortfolio } from '@repo/services'
import { useTranslate } from '@repo/shared/hooks'
import {
  Button,
  Card,
  CenterBox,
  Col,
  Row,
  Typography,
} from '@repo/ui/components'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import Image from 'next/image'
import { useSearchParams } from 'next/navigation'
import QRCode from 'qrcode'
import { useCallback, useEffect, useMemo, useState } from 'react'

export const CertificateContainer = () => {
  const { t, tRich } = useTranslate()
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('')
  const [qrCodeError, setQrCodeError] = useState<string>('')
  const [isGeneratingPDF, setIsGeneratingPDF] = useState<boolean>(false)
  const searchParams = useSearchParams()
  const projectToken = searchParams.get('project') || ''

  // Fetch portfolio data from API
  const {
    data: { data: { accounts = [], projects = [] } = {} } = {},
    isLoading,
    error,
  } = useUserPortfolio()

  // Fetch user auth details for certificate holder name
  const { data: authDetails } = useUserAuthDetails()

  // Find the specific project and user's project account
  const project: UserProject | undefined = projects?.find(
    (p: UserProject) => p.token === projectToken,
  )

  const userProject: UserAccountPortfolio | undefined = accounts?.find(
    (account: UserAccountPortfolio) =>
      account.currency === projectToken && account.type === 'project',
  )

  // Transform portfolio data to certificate format
  const certificateData = useMemo(() => {
    if (!project || !userProject) return null

    return {
      propertyInfo: {
        certificateHolder:
          `${authDetails?.data?.details?.firstName || ''} ${
            authDetails?.data?.details?.lastName || ''
          }`.trim() || '---',
        propertyUsageType: t('filters_usage_' + project.type) || '---', // 'واحد مسکونی',
        address: project.location?.city?.name || '---', // 'تهران',
        constructionStatus:
          project.details?.find((x) => x.key === 'construction_status')
            ?.value || '---', //'سند تک برگ',
        buildingNumber:
          project.details?.find((x) => x.key === 'building_number')?.value ||
          '---', //'۱۶',
        owner: project.details?.find((x) => x.key === 'owner')?.value || '---', //'شخص حقیقی',
        landPlotDetails:
          project.details?.find((x) => x.key === 'land_plot_details')?.value ||
          '---', //'قطعه ۸۲ پلاک ۱۹',
        ownershipDeedStatus:
          project.details?.find((x) => x.key === 'ownership_deed_status')
            ?.value || '---', //'آزاد',
        floor: project.details?.find((x) => x.key === 'floor')?.value || '---', //'۹ از ۱۲',
        projectName:
          project.details?.find((x) => x.key === 'project_name')?.value ||
          '---', //,
        amenities:
          project.details?.find((x) => x.key === 'amenities')?.value || '---', //'پارکینگ، لابی و روف گاردن',
        country: 'ایران',
        internalArea:
          project.details?.find((x) => x.key === 'internal_area')?.value ||
          '---', //162.5,
        city: project.location?.city?.name || '---', // 'تهران',
        balconyArea:
          project.details?.find((x) => x.key === 'balcony_area')?.value ||
          '---', //6.3,
        district: project.location?.district?.name || '---', // '۲۲',
        totalArea:
          project.details?.find((x) => x.key === 'total_area')?.value || '---', // 168.8,
        commonAreaShare:
          project.details?.find((x) => x.key === 'common_area_share')?.value ||
          '---', // 'دارد',
      },
      tokenInfo: {
        totalTokens:
          (project.circulation?.otcCirculating || 0).toString() || '---', // ,
        tokenValue:
          (project.price?.priceRange24h?.last || 0).toString() || '---', // ,
        totalPropertyValue:
          (project.market?.maxSupply || 0).toString() || '---', //,
        certificateNumber: project.id || '---', //,
        issuanceDate: new Date().toLocaleDateString('fa-IR') || '--', // '۱۴۰۴/۰۵/۰۶',
      },
    }
  }, [
    project,
    userProject,
    authDetails?.data?.details?.firstName,
    authDetails?.data?.details?.lastName,
    t,
  ])

  const formatNumber = (num: number) => {
    return num.toLocaleString('fa-IR')
  }

  // Generate QR code based on current page URL
  useEffect(() => {
    const generateQRCode = async () => {
      try {
        // Get the current page URL
        const currentUrl = window.location.href

        // Generate QR code as data URL
        const qrDataUrl = await QRCode.toDataURL(currentUrl, {
          width: 160,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF',
          },
        })

        setQrCodeDataUrl(qrDataUrl)
        setQrCodeError('')
      } catch (error) {
        console.error('Error generating QR code:', error)
        setQrCodeError('Failed to generate QR code')
      }
    }

    // Only generate QR code on client side
    if (typeof window !== 'undefined') {
      generateQRCode()
    }
  }, [])

  // Simple PDF generation
  const generatePDF = useCallback(async () => {
    setIsGeneratingPDF(true)
    try {
      const element = document.getElementById('certificate-container')
      if (!element) {
        throw new Error('Certificate container not found')
      }
      const alertContainer = document.getElementById('alert-container')
      if (alertContainer) {
        alertContainer.style.paddingTop = '0px'
        alertContainer.style.marginTop = '30px'
      }

      const titleContainer = document.getElementById('title-container')
      if (titleContainer) {
        titleContainer.style.marginTop = '10px'
      }

      const mainContainer = document.getElementById('certificate-main')
      if (mainContainer) {
        mainContainer.style.width = '1322px'
      }

      // Convert images to base64 to avoid CORS issues
      const images = element.querySelectorAll('img')

      for (const img of images) {
        if (img.src && !img.src.startsWith('data:')) {
          img.style.marginBottom = '0px'

          try {
            const response = await fetch(img.src)
            const blob = await response.blob()
            const reader = new FileReader()
            const base64 = await new Promise<string>((resolve) => {
              reader.onload = () => resolve(reader.result as string)
              reader.readAsDataURL(blob)
            })
            img.src = base64
          } catch (error) {
            console.log('Failed to convert image to base64:', error)
            // Continue with original image
          }
        }
      }

      // Wait for all images to load
      await Promise.all(
        Array.from(images).map((img) => {
          return new Promise((resolve) => {
            if (img.complete) {
              resolve(true)
            } else {
              img.onload = () => resolve(true)
              img.onerror = () => resolve(true) // Continue even if image fails
            }
          })
        }),
      )

      // Add a small delay to ensure all images are rendered
      await new Promise((resolve) => setTimeout(resolve, 500))

      // Create canvas with images - now that images are base64, CORS should work
      const canvas = await html2canvas(element, {
        scale: 1,
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#DBD3E5',
        logging: false,
        imageTimeout: 15000,
        removeContainer: true,
      })

      // Convert canvas to image data - should work now with base64 images
      const imgData = canvas.toDataURL('image/png', 1)

      const pdf = new jsPDF('p', 'mm', 'a4')

      // A4 dimensions: 210mm x 297mm
      const pageWidth = 210
      const pageHeight = 297

      // Calculate image dimensions to fit A4 properly
      const canvasAspectRatio = canvas.width / canvas.height
      const pageAspectRatio = pageWidth / pageHeight

      let imgWidth, imgHeight, x, y

      if (canvasAspectRatio > pageAspectRatio) {
        // Canvas is wider than A4, fit to width
        imgWidth = pageWidth
        imgHeight = pageWidth / canvasAspectRatio
        x = 0
        y = (pageHeight - imgHeight) / 2
      } else {
        // Canvas is taller than A4, fit to height
        imgHeight = pageHeight
        imgWidth = pageHeight * canvasAspectRatio
        x = (pageWidth - imgWidth) / 2
        y = 0
      }

      pdf.addImage(imgData, 'PNG', x, y, imgWidth, imgHeight)

      // Generate filename with current date
      const currentDate = new Date()
        .toLocaleDateString('fa-IR')
        .replace(/\//g, '-')
      const filename = `certificate-${currentDate}.pdf`

      pdf.save(filename)

      if (alertContainer) {
        alertContainer.style.paddingTop = '20px'
        alertContainer.style.marginTop = '20px'
      }
      if (titleContainer) {
        titleContainer.style.marginTop = '30px'
      }
      if (mainContainer) {
        mainContainer.style.width = 'auto'
      }
    } catch (error) {
      console.error('PDF generation error:', error)

      // Fallback: Use browser print
      try {
        window.print()
      } catch (printError) {
        console.error('Print also failed:', printError)
        // alert(`خطا در تولید PDF: ${error.message || 'خطای نامشخص'}`)
      }
    } finally {
      setIsGeneratingPDF(false)
    }
  }, [])

  // Show loading state
  if (isLoading) {
    return (
      <div className='min-h-screen p-lg !text-[#312640]'>
        <div className='max-w-container-xl mx-auto'>
          <Card className='bg-[#DBD3E5] border-4 border-[#9066CC] rounded-none p-2xl'>
            <div className='text-center'>
              <Typography variant='p' className='text-lg'>
                {t('common_loading')}
              </Typography>
            </div>
          </Card>
        </div>
      </div>
    )
  }

  // Show error state
  if (error || !project || !userProject || !certificateData) {
    return (
      <div className='min-h-screen p-lg !text-[#312640]'>
        <div className='max-w-container-xl mx-auto'>
          <Card className='bg-[#DBD3E5] border-4 border-[#9066CC] rounded-none p-2xl'>
            <div className='text-center'>
              <Typography variant='p' className='text-lg text-red-600'>
                {t('common_error_loading_data')}
              </Typography>
            </div>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div
      id='certificate-main'
      className='min-h-screen p-lg !text-[#312640] !max-w-[1322px] mx-auto'
    >
      <div className='max-w-container-xl mx-auto'>
        <Card
          id='certificate-container'
          className='bg-[#DBD3E5] rounded-none'
          style={{
            backgroundImage: 'url(/Vector.svg)',
            backgroundRepeat: 'repeat',
            backgroundPosition: 'bottom left',
            border: '4px solid #9066CC',
          }}
        >
          {/* Header */}
          <div className='text-center p-2xl'>
            <Image
              alt='logo'
              src='/fav/android-chrome-192x192.png'
              width={80}
              height={80}
              className='m-auto'
            />
            <Image
              alt='logo'
              src='/logo-text.svg'
              width={125}
              height={56}
              className='object-contain max-w-[125px] mt-sm m-auto'
            />
            <div
              id='title-container'
              style={{
                marginTop: '72px',
              }}
            >
              <Typography
                variant='p'
                className='text-[52px] !font-bold !text-[#36264D] !font-abar'
              >
                {t('certificate_title')}
              </Typography>
            </div>
            <div
              id='alert-container'
              style={{
                backgroundColor: '#ffffff40',
                border: '1px solid #9066CC',
                padding: '20px',
              }}
              className='!rounded-lg'
            >
              <span className='text-[18px] !text-[#312640] !font-abar text-center'>
                {tRich.rich('certificate_alert', {
                  date: new Date().toLocaleDateString('fa-IR'),
                })}
              </span>
            </div>
          </div>

          <div className='p-2xl'>
            <div className='mb-md'>
              <div className='mb-md'>
                <Typography
                  variant='p'
                  className='!font-abar-low text-[18px] !font-bold'
                >
                  {t('certificate_property_info')}
                </Typography>
              </div>

              <div className='grid grid-cols-1 md:grid-cols-2 gap-x-6xl gap-y-xs'>
                <div className='mb-md flex justify-between'>
                  <Typography
                    variant='p'
                    className='text-[20px] !font-abar-low'
                  >
                    {t('certificate_holder')}
                  </Typography>
                  <Typography
                    variant='p'
                    className='!font-abar-low text-[20px] !font-bold'
                  >
                    {certificateData.propertyInfo.certificateHolder}
                  </Typography>
                </div>

                <div className='mb-md flex justify-between'>
                  <Typography
                    variant='p'
                    className='text-[20px] !font-abar-low'
                  >
                    {t('certificate_address')}
                  </Typography>
                  <Typography
                    variant='p'
                    className='!font-abar-low text-[20px] !font-bold'
                  >
                    {certificateData.propertyInfo.address}
                  </Typography>
                </div>

                <div className='mb-md flex justify-between'>
                  <Typography
                    variant='p'
                    className='text-[20px] !font-abar-low'
                  >
                    {t('certificate_property_usage_type')}
                  </Typography>
                  <Typography
                    variant='p'
                    className='!font-abar-low text-[20px] !font-bold'
                  >
                    {certificateData.propertyInfo.propertyUsageType}
                  </Typography>
                </div>

                <div className='mb-md flex justify-between'>
                  <Typography
                    variant='p'
                    className='text-[20px] !font-abar-low'
                  >
                    {t('certificate_building_number')}
                  </Typography>
                  <Typography
                    variant='p'
                    className='!font-abar-low text-[20px] !font-bold'
                  >
                    {certificateData.propertyInfo.buildingNumber}
                  </Typography>
                </div>

                <div className='mb-md flex justify-between'>
                  <Typography
                    variant='p'
                    className='text-[20px] !font-abar-low'
                  >
                    {t('certificate_construction_status')}
                  </Typography>
                  <Typography
                    variant='p'
                    className='!font-abar-low text-[20px] !font-bold'
                  >
                    {certificateData.propertyInfo.constructionStatus}
                  </Typography>
                </div>

                <div className='mb-md flex justify-between'>
                  <Typography
                    variant='p'
                    className='text-[20px] !font-abar-low'
                  >
                    {t('certificate_owner')}
                  </Typography>
                  <Typography
                    variant='p'
                    className='!font-abar-low text-[20px] !font-bold'
                  >
                    {certificateData.propertyInfo.owner}
                  </Typography>
                </div>

                <div className='mb-md flex justify-between'>
                  <Typography
                    variant='p'
                    className='text-[20px] !font-abar-low'
                  >
                    {t('certificate_land_plot_details')}
                  </Typography>
                  <Typography
                    variant='p'
                    className='!font-abar-low text-[20px] !font-bold'
                  >
                    {certificateData.propertyInfo.landPlotDetails}
                  </Typography>
                </div>

                <div className='mb-md flex justify-between'>
                  <Typography
                    variant='p'
                    className='text-[20px] !font-abar-low'
                  >
                    {t('certificate_ownership_deed_status')}
                  </Typography>
                  <Typography
                    variant='p'
                    className='!font-abar-low text-[20px] !font-bold'
                  >
                    {certificateData.propertyInfo.ownershipDeedStatus}
                  </Typography>
                </div>

                <div className='mb-md flex justify-between'>
                  <Typography
                    variant='p'
                    className='text-[20px] !font-abar-low'
                  >
                    {t('certificate_floor')}
                  </Typography>
                  <Typography
                    variant='p'
                    className='!font-abar-low text-[20px] !font-bold'
                  >
                    {certificateData.propertyInfo.floor}
                  </Typography>
                </div>

                <div className='mb-md flex justify-between'>
                  <Typography
                    variant='p'
                    className='text-[20px] !font-abar-low'
                  >
                    {t('certificate_project_name')}
                  </Typography>
                  <Typography
                    variant='p'
                    className='!font-abar-low text-[20px] !font-bold'
                  >
                    {certificateData.propertyInfo.projectName}
                  </Typography>
                </div>

                <div className='mb-md flex justify-between'>
                  <Typography
                    variant='p'
                    className='text-[20px] !font-abar-low'
                  >
                    {t('certificate_amenities')}
                  </Typography>
                  <Typography
                    variant='p'
                    className='!font-abar-low text-[20px] !font-bold'
                  >
                    {certificateData.propertyInfo.amenities}
                  </Typography>
                </div>

                <div className='mb-md flex justify-between'>
                  <Typography
                    variant='p'
                    className='text-[20px] !font-abar-low'
                  >
                    {t('certificate_country')}
                  </Typography>
                  <Typography
                    variant='p'
                    className='!font-abar-low text-[20px] !font-bold'
                  >
                    {certificateData.propertyInfo.country}
                  </Typography>
                </div>

                <div className='mb-md flex justify-between'>
                  <Typography
                    variant='p'
                    className='text-[20px] !font-abar-low'
                  >
                    {t('certificate_internal_area')}
                  </Typography>

                  <Typography
                    variant='p'
                    className='!font-abar-low text-[20px] !font-bold'
                  >
                    {certificateData.propertyInfo.internalArea}
                  </Typography>
                </div>

                <div className='mb-md flex justify-between'>
                  <Typography
                    variant='p'
                    className='text-[20px] !font-abar-low'
                  >
                    {t('certificate_city')}
                  </Typography>
                  <Typography
                    variant='p'
                    className='!font-abar-low text-[20px] !font-bold'
                  >
                    {certificateData.propertyInfo.city}
                  </Typography>
                </div>

                <div className='mb-md flex justify-between'>
                  <Typography
                    variant='p'
                    className='text-[20px] !font-abar-low'
                  >
                    {t('certificate_balcony_area')}
                  </Typography>

                  <Typography
                    variant='p'
                    className='!font-abar-low text-[20px] !font-bold'
                  >
                    {certificateData.propertyInfo.balconyArea}{' '}
                    <span className='!font-regular !font-abar-low opacity-50'>
                      {tRich.rich('common_meter_square', {
                        value: () => '',
                      })}
                    </span>
                  </Typography>
                </div>

                <div className='mb-md flex justify-between'>
                  <Typography
                    variant='p'
                    className='text-[20px] !font-abar-low'
                  >
                    {t('certificate_district')}
                  </Typography>
                  <Typography
                    variant='p'
                    className='!font-abar-low text-[20px] !font-bold'
                  >
                    {certificateData.propertyInfo.district}
                  </Typography>
                </div>

                <div className='mb-md flex justify-between'>
                  <Typography
                    variant='p'
                    className='text-[20px] !font-abar-low'
                  >
                    {t('certificate_total_area')}
                  </Typography>

                  <Typography
                    variant='p'
                    className='!font-abar-low text-[20px] !font-bold'
                  >
                    {certificateData.propertyInfo.totalArea}{' '}
                    <span className='!font-regular !font-abar-low opacity-50'>
                      {tRich.rich('common_meter_square', {
                        value: () => '',
                      })}
                    </span>
                  </Typography>
                </div>

                <div className='mb-md flex justify-between'>
                  <Typography
                    variant='p'
                    className='text-[20px] !font-abar-low'
                  >
                    {t('certificate_common_area_share')}
                  </Typography>
                  <Typography
                    variant='p'
                    className='!font-abar-low text-[20px] !font-bold'
                  >
                    {certificateData.propertyInfo.commonAreaShare}{' '}
                    <span className='!font-regular !font-abar-low opacity-50'>
                      {tRich.rich('common_meter_square', {
                        value: () => '',
                      })}
                    </span>
                  </Typography>
                </div>
              </div>
            </div>

            {/* Token Information Section */}
            <div
              style={{
                backgroundColor: '#ffffff40',
              }}
              className='!mb-md !p-lg !rounded-lg !mt-2xl'
            >
              <Typography
                variant='p'
                className=' text-2xl !font-abar-low !font-bold text-[18px] mb-lg pb-sm'
              >
                {t('certificate_token_info')}
              </Typography>

              <div className='  grid grid-cols-1 md:grid-cols-1 gap-xs'>
                <div className=' mb-md flex justify-between'>
                  <Typography
                    variant='p'
                    className='  text-[20px] !font-abar-low'
                  >
                    {t('certificate_total_tokens')}
                  </Typography>
                  <Typography
                    variant='p'
                    className=' text-[20px] !font-abar-low !font-bold'
                  >
                    {certificateData.tokenInfo.totalTokens !== '---' &&
                      formatNumber(
                        Number(certificateData.tokenInfo.totalTokens),
                      )}
                  </Typography>
                </div>

                <div className='  mb-md flex justify-between'>
                  <Typography
                    variant='p'
                    className='  text-[20px] !font-abar-low'
                  >
                    {tRich.rich('certificate_token_value', {
                      time: (time) => (
                        <Typography
                          color='text-neutral-surface-60'
                          className=''
                        >
                          {time}
                        </Typography>
                      ),
                    })}
                  </Typography>
                  <Typography
                    variant='p'
                    className=' text-[20px] !font-abar-low !font-bold'
                  >
                    {certificateData.tokenInfo.tokenValue !== '---' &&
                      formatNumber(
                        Number(certificateData.tokenInfo.tokenValue),
                      )}{' '}
                    <span className='!font-regular opacity-50'>ریال</span>
                  </Typography>
                </div>

                <div className=' mb-md flex justify-between'>
                  <Typography
                    variant='p'
                    className=' text-[20px] !font-abar-low'
                  >
                    {t('certificate_total_property_value')}
                  </Typography>
                  <Typography
                    variant='p'
                    className=' text-[20px] !font-abar-low !font-bold'
                  >
                    {certificateData.tokenInfo.totalPropertyValue !== '---' &&
                      formatNumber(
                        Number(certificateData.tokenInfo.totalPropertyValue),
                      )}{' '}
                    <span className='!font-regular opacity-50'>ریال</span>
                  </Typography>
                </div>

                {/* <div className='mb-md flex justify-between'>
                  <Typography
                    variant='p'
                    className='text-[20px] !font-abar-low'
                  >
                    {t('certificate_certificate_number')}
                  </Typography>
                  <Typography
                    variant='p'
                    className='text-[20px] !font-abar-low !font-bold'
                  >
                    {certificateData.tokenInfo.certificateNumber}
                  </Typography>
                </div> */}

                <div className=' mb-md flex justify-between'>
                  <Typography
                    variant='p'
                    className=' text-[20px] !font-abar-low'
                  >
                    {t('certificate_issuance_date')}
                  </Typography>
                  <Typography
                    variant='p'
                    className=' text-[20px] !font-abar-low !font-bold'
                  >
                    {certificateData.tokenInfo.issuanceDate}
                  </Typography>
                </div>
              </div>
            </div>

            {/* Disclaimers Section */}

            <Col className='mb-md p-lg gap-y-md text-center'>
              <Typography
                variant='p'
                className='leading-relaxed text-[16px] !font-abar-low'
              >
                {t('certificate_disclaimer_1')}
              </Typography>
              <Typography
                variant='p'
                className='leading-relaxed text-[16px] !font-abar-low'
              >
                {t('certificate_disclaimer_2')}
              </Typography>
              <Typography
                variant='p'
                className='leading-relaxed text-[16px] !font-abar-low'
              >
                {t('certificate_disclaimer_3')}
              </Typography>
              <Typography
                variant='p'
                className='leading-relaxed text-[16px] !font-abar-low'
              >
                {t('certificate_disclaimer_4')}
              </Typography>
              <Typography
                variant='p'
                className='leading-relaxed text-[16px] !font-abar-low'
              >
                {t('certificate_disclaimer_5')}
              </Typography>
            </Col>

            {/* QR Code and Identifier */}
            <div className='text-center opacity-0'>
              <div className='inline-block bg-white p-xs rounded-md shadow-md mb-md'>
                <CenterBox className='w-[160px] h-[160px]'>
                  {qrCodeDataUrl ? (
                    <Image
                      src={qrCodeDataUrl}
                      alt='qr-code'
                      width={160}
                      height={160}
                    />
                  ) : qrCodeError ? (
                    <div className='w-[160px] h-[160px] flex items-center justify-center bg-gray-100 text-gray-500 text-xs text-center p-2'>
                      {qrCodeError}
                    </div>
                  ) : (
                    <div className='w-[160px] h-[160px] flex items-center justify-center bg-gray-100 text-gray-500 text-xs'>
                      Loading QR Code...
                    </div>
                  )}
                </CenterBox>
              </div>
              <Typography
                variant='p'
                className='font-mono text-sm !text-[#475950]'
              >
                3f5b7e9a1c2d4e6f8a9b0c1d2e3f4a5b6c7d8e9f0
              </Typography>
            </div>
          </div>
        </Card>
        {/* PDF Download Button */}
        <Row className='justify-center p-md gap-md'>
          <Button
            onClick={generatePDF}
            disabled={isGeneratingPDF}
            variant='ghost'
          >
            {isGeneratingPDF
              ? t('common_loading')
              : t('certificate_download_pdf')}
          </Button>
        </Row>
      </div>
    </div>
  )
}
